// utils/TaskOrderProcessor.ts
import {EntityID, Task} from '../models/types';
import {OrderManager} from './OrderManager';

export class TaskOrderProcessor {
	private static instance: TaskOrderProcessor;
	private cache = new Map<string, string[]>(); // columnId -> sortedTaskIds
	private versionMap = new Map<string, number>(); // columnId -> version
	private taskMapCache = new Map<string, Map<string, Task>>(); // cacheKey -> taskId -> Task

	private constructor() {
	}
	static reset(){
		(TaskOrderProcessor as any).instance = undefined;
	}

	static getInstance(): TaskOrderProcessor {
		if (!TaskOrderProcessor.instance) {
			TaskOrderProcessor.instance = new TaskOrderProcessor();
		}
		return TaskOrderProcessor.instance;
	}

	/**
	 * Получить отсортированные ID задач для конкретной колонки
	 */
	getSortedTaskIds(columnId: string, allTasks: Task[]): string[] {
		const currentVersion = this.getVersion(columnId);
		const cacheKey = `${columnId}_v${currentVersion}`;

		// Проверяем кэш
		const cached = this.cache.get(cacheKey);
		if (cached) {
			return cached;
		}

		// Вычисляем порядок на основе Task.order
		const sortedTasks = allTasks
			.filter(task => task.columnId === columnId)
			.sort((a, b) => a.order - b.order);

		const sortedIds = sortedTasks.map(task => task.id);

		// Сохраняем в кэш
		this.cache.set(cacheKey, sortedIds);

		return sortedIds;
	}

	/**
	 * Получить мапу задач по ID для оптимизации поиска
	 */
	getTaskMap(allTasks: Task[]): Map<string, Task> {
		// Создаем уникальный ключ для кэша на основе длины массива и максимальной даты обновления
		const maxUpdateTime = allTasks.reduce((max, task) =>
			new Date(task.updatedAt).getTime() > max ? new Date(task.updatedAt).getTime() : max, 0);
		const cacheKey = `tasks_${allTasks.length}_${maxUpdateTime}`;

		const cached = this.taskMapCache.get(cacheKey);
		if (cached) {
			return cached;
		}

		// Создаем мапу задач
		const taskMap = new Map<string, Task>();
		for (const task of allTasks) {
			taskMap.set(task.id, task);
		}

		// Сохраняем в кэш
		this.taskMapCache.set(cacheKey, taskMap);

		return taskMap;
	}

	/**
	 * Обновить порядок задач в колонке
	 */
	async updateColumnOrder(
		columnId: EntityID,
		allTasks: Task[],
		taskStorage: any // TaskStorage instance
	): Promise<Task[]> {
		const columnTasks = allTasks.filter(task => task.columnId === columnId);
		const needsReorder = OrderManager.needsReorder(columnTasks);

		if (!needsReorder) {
			return columnTasks;
		}

		// Вариант А: Сначала вычисляем новые order для всех задач в колонке
		const redistributed = OrderManager.redistributeOrders(columnTasks);

		// Потом обновляем в памяти
		for (const redistTask of redistributed) {
			const existingTask = allTasks.find(t => t.id === redistTask.id);
			if (existingTask) {
				existingTask.order = redistTask.order;
				existingTask.updatedAt = new Date();
				existingTask.isSynced = false;
			}
		}

		// Потом сохраняем в БД
		for (const redistTask of redistributed) {
			await taskStorage.updateTaskOptimistic(redistTask.id, {
				order: redistTask.order,
				updatedAt: new Date(),
				isSynced: false,
			});
		}

		// Инвалидируем кэш для колонки
		this.invalidateColumn(columnId);

		return redistributed;
	}

	/**
	 * Обновить позицию задачи в колонке
	 */
	async updateTaskPosition(
		taskId: string,
		targetColumnId: string,
		targetIndex: number,
		allTasks: Task[],
		taskStorage: any // TaskStorage instance
	): Promise<void> {
		const task = allTasks.find(t => t.id === taskId);
		if (!task) {
			throw new Error(`Task ${taskId} not found`);
		}

		const oldColumnId = task.columnId;
		const isNewColumn = oldColumnId !== targetColumnId;

		// Регистрация (обновление меток времени и т.д.)
		task.updatedAt = new Date();
		task.isSynced = false;

		// Если колонка меняется, обновляем поле columnId
		if (isNewColumn) {
			task.columnId = targetColumnId;
		}

		// Получить задачи целевой колонки (без текущей задачи, если она меняет колонку)
		const targetColumnTasks = allTasks.filter(t =>
			t.columnId === targetColumnId && t.id !== taskId
		);

		// Вычислить новый order (реальная позиция задачи)
		const adjacent = OrderManager.getAdjacentTasks(targetColumnTasks, targetIndex);
		let newOrder = OrderManager.getOrderBetweenTasks(adjacent.prevTask, adjacent.nextTask);

		// Если нужна перевыдача order
		if (newOrder === -1) {
			// Добавить задачу в правильную позицию для перевыдачи
			const tasksForRedistribution = [...targetColumnTasks, task];
			const sorted = OrderManager.sortTasksByOrder(tasksForRedistribution);
			// Вставить задачу в нужную позицию
			const finalSorted = [
				...sorted.slice(0, targetIndex),
				task,
				...sorted.slice(targetIndex)
			];
			const redistributed = OrderManager.redistributeOrders(finalSorted);
			newOrder = redistributed.find(t => t.id === task.id)?.order || 0;

			// Обновить order для всех задач целевой колонки (кроме текущей)
			for (const redistTask of redistributed) {
				if (redistTask.id === task.id) continue;

				const existingTask = allTasks.find(t => t.id === redistTask.id);
				if (existingTask) {
					existingTask.order = redistTask.order;
					existingTask.updatedAt = new Date();
					existingTask.isSynced = false;
				}
			}
		}

		// Установить новый order для текущей задачи
		task.order = newOrder;

		// Обновить order для целевой колонки (если нужно)
		await this.updateColumnOrder(targetColumnId, allTasks, taskStorage);

		// Обновить order для старой колонки (если задача меняет колонку)
		// if (isNewColumn) {
		// 	await this.updateColumnOrder(oldColumnId, allTasks, taskStorage);
		// }
	}

	/**
	 * Инвалидация кэша для конкретной колонки
	 */
	public invalidateColumn(columnId: string): void {
		const newVersion = (this.versionMap.get(columnId) || 0) + 1;
		this.versionMap.set(columnId, newVersion);

		// Удаляем устаревшие записи из кэша
		for (const key of this.cache.keys()) {
			if (key.startsWith(`${columnId}_`)) {
				this.cache.delete(key);
			}
		}
	}

	/**
	 * Инвалидация кэша для нескольких колонок
	 */
	invalidateColumns(columnIds: string[]): void {
		for (const columnId of columnIds) {
			this.invalidateColumn(columnId);
		}
	}

	/**
	 * Получить версию кэша для колонки
	 */
	private getVersion(columnId: string): number {
		return this.versionMap.get(columnId) || 0;
	}

	/**
	 * Полная инвалидация кэша
	 */
	invalidateAll(): void {
		this.cache.clear();
		this.versionMap.clear();
		this.taskMapCache.clear();
	}
}